import { useState, useEffect, useRef, useMemo } from 'react';
import client from '../api/client';

const EMPTY_EXTRA_FIELDS = {};

export function useFeeSettings({ autoSave = false, extraSaveFields = EMPTY_EXTRA_FIELDS, onLoad } = {}) {
  const [brokerFee, setBrokerFee] = useState(0);
  const [salesTax, setSalesTax] = useState(0);
  const [sccBuy, setSccBuy] = useState(false);
  const [sccSell, setSccSell] = useState(false);
  const settingsLoaded = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.get('/eve/skillfarm/settings/');
        setBrokerFee(res.data.broker_fee);
        setSalesTax(res.data.sales_tax);
        setSccBuy(res.data.scc_buy);
        setSccSell(res.data.scc_sell);
        if (onLoad) onLoad(res.data);
      } catch {
        // Not logged in or first visit — keep defaults
      }
      settingsLoaded.current = true;
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoSave || !settingsLoaded.current) return;
    const timer = setTimeout(() => {
      client.post('/eve/skillfarm/settings/update/', {
        broker_fee: brokerFee,
        sales_tax: salesTax,
        scc_buy: sccBuy,
        scc_sell: sccSell,
        ...extraSaveFields,
      }).catch(err => console.warn('Failed to save skill farm settings:', err));
    }, 500);
    return () => clearTimeout(timer);
  }, [autoSave, brokerFee, salesTax, sccBuy, sccSell, extraSaveFields]);

  const { buyCostMultiplier, sellRevenueMultiplier } = useMemo(() => {
    const brokerRate = brokerFee / 100;
    const taxRate = salesTax / 100;
    const sccBuyRate = sccBuy ? 0.005 : 0;
    const sccSellRate = sccSell ? 0.005 : 0;
    return {
      buyCostMultiplier: 1 + brokerRate + sccBuyRate,
      sellRevenueMultiplier: 1 - taxRate - brokerRate - sccSellRate,
    };
  }, [brokerFee, salesTax, sccBuy, sccSell]);

  return {
    brokerFee, setBrokerFee,
    salesTax, setSalesTax,
    sccBuy, setSccBuy,
    sccSell, setSccSell,
    buyCostMultiplier,
    sellRevenueMultiplier,
    settingsLoaded,
  };
}
